import styles from './Input.module.scss'
import { InputContext, InputProvider } from './InputContext'
import { PATTERNS } from '@/constants'
import { XMarkIcon } from '@heroicons/react/24/outline'
import cn from 'classnames'
import { Children, cloneElement, forwardRef, useContext, useEffect, useState } from 'react'
import getValidityState from '@/utils/getValidityState'
import renderHeroIcon from '@/utils/renderHeroIcon'
import uuid from '@/utils/uuid'

const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+\.[A-Za-z]{2,}$/

function InputRoot(
  { className, type, icon, onInvalid, uuid, options = [], errorMessage = null, objectKey, objectValue, ...props },
  ref
) {
  const { valid, invalid, errorMessage: error } = useContext(InputContext)
  const [isValid, setIsValid] = valid
  const [isInvalid, setIsInvalid] = invalid
  const [_, setErrorMessage] = error
  const [hasFile, setHasFile] = useState(false)
  const Icon = renderHeroIcon('outline', icon)

  function testEmail(email) {
    const pattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
    const isValid = pattern.test(email)

    return isValid
  }

  function setInvalid() {
    setIsValid(false)
    setIsInvalid(true)
  }

  function setValid() {
    setIsInvalid(false)
    setIsValid(true)
  }

  function shouldValidate(event) {
    return !event.currentTarget.closest('form').hasAttribute('novalidate')
  }

  useEffect(() => {
    if (!errorMessage) return

    setInvalid()
    setErrorMessage(errorMessage[0])
  }, [errorMessage])

  return (
    <div
      className={cn(styles['input'], {
        [styles['input--valid']]: isValid,
        [styles['input--invalid']]: isInvalid,
        [styles['input--icon']]: Icon,
        [styles[`input--${type}`]]: type,
        [styles[`input--file`]]: type === 'file',
        [styles[`input--file-added`]]: hasFile && type === 'file',
        [className]: className,
      })}
    >
      {Icon && <Icon />}

      {type === 'select' && (
        <select
          ref={ref}
          id={uuid}
          className={cn(styles['input__main'])}
          defaultValue={props.defaultValue || {}}
          onInvalid={(event) => {
            event.preventDefault()
            setInvalid()
            setErrorMessage(getValidityState(event))
          }}
          onInput={(event) => {
            if (!shouldValidate(event)) return
            event.target.checkValidity()
            if (event.target.validity.valid) return setValid()
          }}
          {...props}
        >
          {props.placeholder && <option value={''}>{props.placeholder}</option>}

          {options.map((option) => (
            <option
              key={option[objectKey]}
              value={option[objectKey]}
            >
              {option[objectValue]}
            </option>
          ))}
        </select>
      )}

      {type === 'textarea' && (
        <textarea
          ref={ref}
          id={uuid}
          rows="3"
          className={cn(styles['input__main'])}
          onInvalid={(event) => {
            event.preventDefault()
            setInvalid()
            setErrorMessage(getValidityState(event))
          }}
          onInput={(event) => {
            if (!shouldValidate(event)) return
            event.target.checkValidity()
            if (event.target.validity.valid) return setValid()
          }}
          {...props}
        />
      )}

      {type === 'email' && (
        <input
          ref={ref}
          type={type}
          id={uuid}
          className={cn(styles['input__main'])}
          pattern={PATTERNS.email}
          onInvalid={(event) => {
            event.preventDefault()
            setIsInvalid(true)
            setIsValid(false)
            setErrorMessage(getValidityState(event))
          }}
          onInput={(event) => {
            if (!shouldValidate(event)) return
            if (testEmail(event.target.value)) return setValid()
            event.target.checkValidity()
          }}
          {...props}
        />
      )}

      {type !== 'select' && type !== 'textarea' && type !== 'email' && (
        <input
          ref={ref}
          type={type}
          id={uuid}
          className={cn(styles['input__main'])}
          onInvalid={(event) => {
            event.preventDefault()
            setInvalid()
            setErrorMessage(getValidityState(event))
          }}
          onInput={(event) => {
            if (type === 'file') setHasFile(true)
            if (!shouldValidate(event)) return
            if (event.target.validity.valid) return setValid()
            event.target.checkValidity()
          }}
          {...props}
        />
      )}

      {type === 'file' && hasFile && (
        <button
          type="button"
          className={cn(styles['input__button'])}
          onClick={() => {
            const input = document.getElementById(uuid)
            input.value = ''
            input.checkValidity()
            setHasFile(false)
          }}
        >
          <XMarkIcon />
        </button>
      )}

      {type === 'file' && !hasFile && (
        <span className={cn(styles['input__label'])}>{props.placeholder || 'Bestand uploaden'}</span>
      )}
    </div>
  )
}

function Label({ className, children, uuid, ...props }) {
  return (
    <label
      htmlFor={uuid}
      className={cn(styles['label'], { [className]: className })}
      {...props}
    >
      {children}
    </label>
  )
}

function Error({ manualInvalid = false, className, children, ...props }) {
  const { invalid, errorMessage: error } = useContext(InputContext)
  const [isInvalid] = invalid
  const [errorMessage] = error

  return (
    <>
      {(isInvalid || manualInvalid) && (
        <span
          className={cn(styles['error'], { [className]: className })}
          {...props}
        >
          {children || errorMessage}
        </span>
      )}
    </>
  )
}

function Box({ className, ...props }) {
  const [children, setChildren] = useState(props.children)

  useEffect(() => {
    const uniqueId = uuid()
    setChildren(Children.toArray(props.children).map((child) => cloneElement(child, { uuid: uniqueId })))
  }, [props.children])

  return (
    <InputProvider>
      <div
        className={cn(styles['root'], { [className]: className })}
        {...props}
      >
        {children}
      </div>
    </InputProvider>
  )
}

const Input = Object.assign(forwardRef(InputRoot), { Box, Label, Error })

export default Input
